import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
// Components
import Loading from "../../components/Loading";
// Hooks
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCollection } from "../../hooks/useCollection";
// Styles
import "../../styles/pages/Contact.css";

function Contact() {
  const { user: currentUser } = useAuthContext();
  const { documents: users, error } = useCollection("users");
  const [benomaUser, setBenomaUser] = useState(null);
  const [otherUsers, setOtherUsers] = useState([]);

  useEffect(() => {
    if (!users) return;

    const benoma = users.find((user) => user.name?.toLowerCase() === "benoma engineering");
    const members = users.filter((user) => user.name?.toLowerCase() !== "benoma engineering");

    setBenomaUser(benoma || null);
    setOtherUsers(members);
  }, [users]);

  if (!users) return <Loading message="Loading contacts" />;

  if (error) {
    return (
      <main className="contact-page d-flex align-items-center">
        <div className="container text-center">
          <span className="font-monospace text-danger d-block mb-3">ERROR / CONTACTS</span>
          <h1 className="fw-bold mb-3">Something went wrong.</h1>
          <p className="text-secondary mb-0">{error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="contact-page text-light">
      {/* Hero */}
      <section className="contact-hero position-relative overflow-hidden">
        <div className="contact-grid position-absolute top-0 start-0 w-100 h-100" />
        <div className="container position-relative">
          <div className="row align-items-end g-5">
            <div className="col-lg-8">
              <span className="section-label d-block mb-4">03 / CONTACT</span>
              <h1 className="contact-title fw-bold mb-4">
                Have something in mind?
                <br />
                <span className="text-danger">Let&apos;s talk.</span>
              </h1>
              <p className="contact-intro text-secondary mb-0">
                Whether it&apos;s a web platform, mobile app, automation workflow, data product or
                an idea that still needs shaping, we&apos;d like to hear about it.
              </p>
            </div>
            <div className="col-lg-4">
              <div className="contact-status border-top border-secondary-subtle pt-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="contact-status-dot" />
                  <small className="font-monospace text-secondary">CURRENT STATUS</small>
                </div>
                <p className="mb-0 fw-semibold">Available for selected projects</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Main Contact */}
      {benomaUser && (
        <section className="contact-primary py-5">
          <div className="container">
            <div className="contact-card overflow-hidden">
              <div className="row g-0">
                <div className="col-lg-5">
                  <div className="contact-brand-panel position-relative h-100 d-flex flex-column justify-content-between p-4 p-md-5">
                    <div className="contact-grid position-absolute top-0 start-0 w-100 h-100" />
                    <div className="position-relative">
                      <small className="font-monospace text-danger fw-bold">
                        BENOMA / GENERAL ENQUIRIES
                      </small>
                    </div>
                    <div className="position-relative py-5">
                      {benomaUser.photoURL ? (
                        <img
                          src={benomaUser.photoURL}
                          alt={benomaUser.name || "Benoma"}
                          className="contact-benoma-logo"
                        />
                      ) : (
                        <div className="contact-benoma-logo contact-benoma-fallback d-flex align-items-center justify-content-center bg-danger text-white fw-bold">
                          B
                        </div>
                      )}
                    </div>
                    <div className="position-relative">
                      <h2 className="display-5 fw-bold mb-2">{benomaUser.name || "Benoma"}</h2>
                      <p className="text-secondary mb-0">
                        Digital products, software and engineering.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-7">
                  <div className="p-4 p-md-5">
                    <div className="mb-5">
                      <span className="section-label d-block mb-3">GET IN TOUCH</span>
                      <h2 className="contact-card-title fw-bold mb-3">
                        Start with a conversation.
                      </h2>
                      <p className="text-secondary contact-card-copy mb-0">
                        Tell us what you&apos;re building, what problem you&apos;re trying to solve,
                        or where you need help.
                      </p>
                    </div>
                    <div className="contact-detail-list border-top border-secondary-subtle">
                      {benomaUser.email && (
                        <a
                          href={`mailto:${benomaUser.email}`}
                          className="contact-detail-row d-flex align-items-center gap-3 py-4 border-bottom border-secondary-subtle text-decoration-none"
                        >
                          <span className="contact-detail-icon d-flex align-items-center justify-content-center">
                            <i className="bi bi-envelope" />
                          </span>
                          <div className="flex-grow-1">
                            <small className="text-secondary font-monospace d-block mb-1">
                              EMAIL
                            </small>
                            <span className="text-light">{benomaUser.email}</span>
                          </div>
                          <span className="contact-arrow">↗</span>
                        </a>
                      )}
                      {benomaUser.phone && (
                        <a
                          href={`tel:+${benomaUser.phone}`}
                          className="contact-detail-row d-flex align-items-center gap-3 py-4 border-bottom border-secondary-subtle text-decoration-none"
                        >
                          <span className="contact-detail-icon d-flex align-items-center justify-content-center">
                            <i className="bi bi-telephone" />
                          </span>
                          <div className="flex-grow-1">
                            <small className="text-secondary font-monospace d-block mb-1">
                              PHONE
                            </small>
                            <span className="text-light">+{benomaUser.phone}</span>
                          </div>
                          <span className="contact-arrow">↗</span>
                        </a>
                      )}
                      {(benomaUser.city || benomaUser.country) && (
                        <div className="contact-detail-row d-flex align-items-center gap-3 py-4 border-bottom border-secondary-subtle">
                          <span className="contact-detail-icon d-flex align-items-center justify-content-center">
                            <i className="bi bi-geo-alt" />
                          </span>
                          <div className="flex-grow-1">
                            <small className="text-secondary font-monospace d-block mb-1">
                              LOCATION
                            </small>
                            <span className="text-light">
                              {[benomaUser.city, benomaUser.country].filter(Boolean).join(", ")}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-4">
                      <Link
                        to="/about"
                        className="btn btn-danger d-inline-flex align-items-center gap-2"
                      >
                        About Benoma
                        <span>→</span>
                      </Link>
                      {currentUser && (
                        <Link to="/edit-profile" className="btn btn-outline-light">
                          Edit Contact
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      {/* Members */}
      <section className="contact-members">
        <div className="container">
          <div className="row align-items-end g-4 mb-5">
            <div className="col-lg-8">
              <span className="section-label d-block mb-3">THE COLLECTIVE</span>
              <h2 className="contact-section-title fw-bold mb-0">
                Meet the people behind the work.
              </h2>
            </div>
            <div className="col-lg-4">
              <p className="text-secondary mb-0">
                Benoma brings together developers, engineers and collaborators with different skills
                and perspectives.
              </p>
            </div>
          </div>
          {otherUsers.length > 0 ? (
            <div className="row g-3">
              {otherUsers.map((member, index) => {
                const isOwner =
                  currentUser?.uid === member.id || currentUser?.email === member.email;
                return (
                  <div key={member.id} className="col-md-6 col-xl-4">
                    <article className="member-card h-100 p-4">
                      <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
                        {member.photoURL ? (
                          <img
                            src={member.photoURL}
                            alt={member.name || "Benoma member"}
                            className="member-avatar rounded-circle"
                          />
                        ) : (
                          <div className="member-avatar member-avatar-fallback rounded-circle d-flex align-items-center justify-content-center fw-bold">
                            {member.name?.charAt(0).toUpperCase() || "?"}
                          </div>
                        )}
                        <small className="member-number font-monospace text-secondary">
                          {String(index + 1).padStart(2, "0")}
                        </small>
                      </div>
                      <div className="mb-4">
                        <h3 className="h4 fw-bold mb-2">{member.name || "Benoma Member"}</h3>
                        {(member.city || member.country) && (
                          <p className="small text-secondary mb-0">
                            <i className="bi bi-geo-alt me-2" />
                            {[member.city, member.country].filter(Boolean).join(", ")}
                          </p>
                        )}
                      </div>
                      <div className="member-contact border-top border-secondary-subtle pt-3">
                        {member.email && (
                          <a
                            href={`mailto:${member.email}`}
                            className="d-block text-secondary text-decoration-none small text-truncate mb-2"
                          >
                            {member.email}
                          </a>
                        )}
                        {member.phone && (
                          <a
                            href={`tel:+${member.phone}`}
                            className="d-block text-secondary text-decoration-none small mb-3"
                          >
                            +{member.phone}
                          </a>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2 mt-auto pt-2">
                        <Link
                          to={`/profile/${encodeURIComponent(member.name)}`}
                          className="btn btn-outline-light btn-sm flex-grow-1"
                        >
                          View Profile
                        </Link>
                        {isOwner && (
                          <Link
                            to="/edit-profile"
                            className="btn btn-outline-danger btn-sm"
                            aria-label="Edit profile"
                          >
                            <i className="bi bi-pencil" />
                          </Link>
                        )}
                      </div>
                    </article>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="border-top border-secondary-subtle py-5 text-center">
              <p className="text-secondary mb-0">No members to display yet.</p>
            </div>
          )}
        </div>
      </section>
      {/* Final CTA */}
      <section className="contact-cta bg-danger text-white">
        <div className="container">
          <div className="row align-items-end g-4">
            <div className="col-lg-9">
              <small className="contact-cta-label font-monospace fw-bold d-block mb-3">
                HAVE A PROJECT?
              </small>
              <h2 className="contact-cta-title fw-bold mb-0">Let&apos;s make something useful.</h2>
            </div>
            <div className="col-lg-3 text-lg-end">
              {benomaUser?.email && (
                <a
                  href={`mailto:${benomaUser.email}`}
                  className="btn btn-light btn-lg d-inline-flex align-items-center gap-2"
                >
                  Email Benoma
                  <span>↗</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Contact;
